import { Alert } from '@warp-ds/react';
import type React from 'react';
import { useState } from 'react';
import { FinnBox } from '@/components/finn/FinnBox';
import { FinnBoxRepublish } from '@/components/finn/FinnBoxRepublish';
import { FinnBoxRepublishWithDropdown } from '@/components/finn/FinnBoxRepublishWithDropdown';
import { FinnBoxWithDropdown } from '@/components/finn/FinnBoxWithDropdown';
import { PageNavigator, PageNumber } from '@/components/pagenavigator/PageNavigator';
import { QasaBox } from '@/components/qasa/QasaBox';
import { QasaBoxRepublish } from '@/components/qasa/QasaBoxRepublish';
import { QasaBoxRepublishWithDropdown } from '@/components/qasa/QasaBoxRepublishWithDropdown';
import { QasaBoxWithDropdown } from '@/components/qasa/QasaBoxWithDropdown';
import { useExperiment } from '@/hooks/experiments';
import { useUnleash } from '@/hooks/unleash';

type Props = {
    csrfMicroServiceToken: string;
    itemId?: string;
    error?: string;
};

interface ScrollEvent extends React.UIEvent<HTMLDivElement> {
    target: HTMLDivElement & EventTarget;
}

const experimentConfig = {
    name: 'travel-qasa-platform-selector-expandable',
    id: 6,
};

const PlatformSelectionPage = ({ csrfMicroServiceToken, itemId, error }: Props) => {
    const experiment = useExperiment();
    const unleash = useUnleash();
    const variant = experiment[experimentConfig.name] ?? 'control';
    const [activeBox, setActiveBox] = useState(PageNumber.ONE);
    const snapCenterStyle = {
        width: '85%',
        scrollSnapAlign: 'center',
    };

    const isTreatment = variant === 'treatment' || unleash.qasaPlatformSelectorExpandableEnabled;

    const QasaComponent = itemId
        ? isTreatment
            ? QasaBoxRepublishWithDropdown
            : QasaBoxRepublish
        : isTreatment
          ? QasaBoxWithDropdown
          : QasaBox;

    const FinnComponent = itemId
        ? isTreatment
            ? FinnBoxRepublishWithDropdown
            : FinnBoxRepublish
        : isTreatment
          ? FinnBoxWithDropdown
          : FinnBox;

    const handleScroll = (e: ScrollEvent): void => {
        const scrollLeft = e.target.scrollLeft;
        const totalWidth = e.target.scrollWidth - e.target.clientWidth;

        if (scrollLeft < totalWidth / 2) {
            setActiveBox(PageNumber.ONE);
        } else {
            setActiveBox(PageNumber.TWO);
        }
    };

    return (
        <main className="mb-64 page-container">
            <h1 className="my-16 md:my-32">{itemId ? 'Republiser din bolig' : 'Hvordan vil du leie ut din bolig?'}</h1>
            {error === 'true' && (
                <div className="mb-16">
                    <Alert type="negative" show>
                        Ushda her gikk noe galt. Vi klarte ikke republisere annonsen på Qasa. Vennligst prøv igjen. Om du har prøvd å
                        republisere denne annonsen tidligere kan du se om den ligger under mine annonser på qasa.com.
                    </Alert>
                </div>
            )}
            <div
                className={'flex flex-row md:space-y-0 space-x-10 md:space-x-16 -mr-16 md:mr-0'}
                onScroll={handleScroll}
                style={{ scrollSnapType: 'x mandatory', overflowX: 'auto' }}>
                <div className="shrink-0 md:shrink md:min-w-0 md:max-w-1/2" style={snapCenterStyle}>
                    <QasaComponent itemId={itemId} experiment={experimentConfig.id} variant={variant} />
                </div>
                <div className="shrink-0 md:shrink md:min-w-0 md:max-w-1/2 mr-16!" style={snapCenterStyle}>
                    <FinnComponent csrfMicroServiceToken={csrfMicroServiceToken} itemId={itemId} variant={variant} />
                </div>
            </div>
            <div id="page-navigator" className="md:hidden mt-24 flex justify-center">
                <PageNavigator activeBox={activeBox} />
            </div>
        </main>
    );
};

export default PlatformSelectionPage;
